import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { logisticsService } from '../services/api';

// Simple signature pad component using canvas
function SignaturePad({ label, onSignatureChange, signatureData }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    isDrawing.current = false;
    const data = canvasRef.current.toDataURL('image/png');
    onSignatureChange(data);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onSignatureChange(null);
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <label style={{ fontWeight: 600, fontSize: 13 }}>{label}</label>
        <button
          type="button"
          onClick={clearSignature}
          style={{
            background: 'none', border: '1px solid #ccc', borderRadius: 4, padding: '2px 8px',
            fontSize: 11, cursor: 'pointer', color: '#666'
          }}
        >
          Limpiar
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{
          border: '2px solid #d1d5db',
          borderRadius: 8,
          cursor: 'crosshair',
          width: '100%',
          height: 120,
          touchAction: 'none',
          background: '#fff'
        }}
      />
      {signatureData && (
        <div style={{ fontSize: 11, color: '#22c55e', marginTop: 2 }}>✓ Firma capturada</div>
      )}
    </div>
  );
}

export default function CarrierDispatchModal({ open, onClose, orders, carrierName, carrierId, onSuccess }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [carrierRepName, setCarrierRepName] = useState('');
  const [observations, setObservations] = useState('');
  const [dispatcherSig, setDispatcherSig] = useState(null);
  const [carrierRepSig, setCarrierRepSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Select all orders by default when modal opens
  useEffect(() => {
    if (open && orders?.length) {
      setSelectedIds(new Set(orders.map(o => o.id)));
      setCarrierRepName('');
      setObservations('');
      setDispatcherSig(null);
      setCarrierRepSig(null);
    }
  }, [open, orders]);

  const toggleOrder = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (selectedIds.size === orders?.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map(o => o.id)));
    }
  }, [selectedIds.size, orders]);

  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      toast.error('Debe seleccionar al menos un pedido');
      return;
    }
    if (!carrierRepName.trim()) {
      toast.error('Ingrese el nombre del representante de la transportadora');
      return;
    }
    if (!dispatcherSig) {
      toast.error('Se requiere la firma del despachador');
      return;
    }
    if (!carrierRepSig) {
      toast.error('Se requiere la firma del representante');
      return;
    }

    setSubmitting(true);
    try {
      const result = await logisticsService.createCarrierDispatchActa({
        carrierName,
        carrierId,
        orderIds: Array.from(selectedIds),
        dispatcherSignature: dispatcherSig,
        carrierRepSignature: carrierRepSig,
        carrierRepName: carrierRepName.trim(),
        observations: observations.trim()
      });

      if (result.blob) {
        // Download PDF
        const url = window.URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${result.actaNumber || 'acta-despacho'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`✅ Acta ${result.actaNumber} generada y descargada`);
      } else {
        toast.success(result.message || 'Acta generada exitosamente');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error creando acta:', err);
      toast.error(err.message || 'Error creando acta de despacho');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const selectedOrders = orders?.filter(o => selectedIds.has(o.id)) || [];
  const totalAmount = selectedOrders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16
    }}
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 700,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f8fafc', borderRadius: '12px 12px 0 0'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
              📋 Cerrar Despacho
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>
              {carrierName} — {new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: '#94a3b8', fontSize: 20
            }}
          >
            <Icons.X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {/* Order list */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                Pedidos ({selectedIds.size}/{orders?.length || 0} seleccionados)
              </span>
              <button
                type="button"
                onClick={toggleAll}
                style={{
                  background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6,
                  padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#334155'
                }}
              >
                {selectedIds.size === orders?.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </button>
            </div>
            <div style={{
              border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 200, overflow: 'auto'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '6px 8px', textAlign: 'center', width: 32 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.size === orders?.length}
                        onChange={toggleAll}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Pedido</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Cliente</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {(orders || []).map(order => (
                    <tr
                      key={order.id}
                      onClick={() => toggleOrder(order.id)}
                      style={{
                        cursor: 'pointer',
                        background: selectedIds.has(order.id) ? '#eff6ff' : 'transparent',
                        borderBottom: '1px solid #f1f5f9'
                      }}
                    >
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(order.id)}
                          onChange={() => toggleOrder(order.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', fontWeight: 600, color: '#1e40af' }}>
                        {order.order_number}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#334155' }}>
                        {order.customer_name}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>
                        ${parseFloat(order.total_amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 0 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Totals */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 16,
              marginTop: 8, fontSize: 13, fontWeight: 600, color: '#1e293b'
            }}>
              <span>Seleccionados: {selectedIds.size}</span>
              <span>Total: ${totalAmount.toLocaleString('es-CO', { minimumFractionDigits: 0 })}</span>
            </div>
          </div>

          {/* Carrier rep name */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>
              Nombre del Representante de {carrierName || 'Transportadora'} *
            </label>
            <input
              type="text"
              value={carrierRepName}
              onChange={(e) => setCarrierRepName(e.target.value)}
              placeholder="Nombre completo del representante"
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: 6, fontSize: 13, outline: 'none'
              }}
            />
          </div>

          {/* Signatures */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <SignaturePad
              label="🖊️ Firma del Despachador"
              signatureData={dispatcherSig}
              onSignatureChange={setDispatcherSig}
            />
            <SignaturePad
              label="🖊️ Firma del Representante"
              signatureData={carrierRepSig}
              onSignatureChange={setCarrierRepSig}
            />
          </div>

          {/* Observations */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>
              Observaciones (opcional)
            </label>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Notas adicionales sobre el despacho..."
              rows={2}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: 6, fontSize: 13, resize: 'vertical', outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f8fafc', borderRadius: '0 0 12px 12px'
        }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 8,
              background: '#fff', cursor: 'pointer', fontSize: 13, color: '#64748b'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selectedIds.size === 0 || !dispatcherSig || !carrierRepSig || !carrierRepName.trim()}
            style={{
              padding: '8px 24px', border: 'none', borderRadius: 8,
              background: submitting || selectedIds.size === 0 ? '#94a3b8' : '#059669',
              color: '#fff', cursor: submitting ? 'wait' : 'pointer',
              fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            {submitting ? (
              <>
                <Icons.Loader2 size={16} className="animate-spin" />
                Generando Acta...
              </>
            ) : (
              <>
                <Icons.FileCheck size={16} />
                Generar Acta y Cerrar Despacho ({selectedIds.size})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
